import React, { useState, useEffect } from 'react';
import { Plus, X, Search, BookOpen } from 'lucide-react';
import { BiblePicker } from './BiblePicker';
import { StudyNote, VerseReference, Category, BibleVersion, Highlight } from '../types';
import { parseVerseReference, generateVerseId } from '../utils/verseParser';
import { fetchBibleVerse, fetchBibleVerseRange, BIBLE_VERSIONS } from '../utils/bibleApi';
import { HighlightableText } from './HighlightableText';

interface NoteEditorProps {
  onSave: (note: StudyNote) => void;
  editingNote?: StudyNote | null;
  onCancel: () => void;
  categories: Category[];
}

export const NoteEditor: React.FC<NoteEditorProps> = ({ onSave, editingNote, onCancel, categories }) => {
  const [verseInput, setVerseInput] = useState(editingNote?.verse.display || '');
  const [verseText, setVerseText] = useState(editingNote?.verse.verseText || '');
  const [content, setContent] = useState(editingNote?.content || '');
  const [bibleVersion, setBibleVersion] = useState<BibleVersion>('NLT');
  const [linkedVerseInput, setLinkedVerseInput] = useState('');
  const [linkedVerseText, setLinkedVerseText] = useState('');
  const [linkedVerseDesc, setLinkedVerseDesc] = useState('');
  const [linkedVerses, setLinkedVerses] = useState<VerseReference[]>(editingNote?.linkedVerses || []);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>(editingNote?.categoryId);
  const [verseHighlights, setVerseHighlights] = useState<Highlight[]>(editingNote?.verseHighlights || []);
  const [noteHighlights, setNoteHighlights] = useState<Highlight[]>(editingNote?.noteHighlights || []);
  const [noteEditMode, setNoteEditMode] = useState<'edit' | 'highlight'>('edit');
  const [linkedVerseHighlights, setLinkedVerseHighlights] = useState<Highlight[]>([]);
  const [isLoadingVerse, setIsLoadingVerse] = useState(false);
  const [isLoadingLinkedVerse, setIsLoadingLinkedVerse] = useState(false);
  const [showMainPicker, setShowMainPicker] = useState(false);
  const [showLinkedPicker, setShowLinkedPicker] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setVerseInput(editingNote?.verse.display || '');
    setVerseText(editingNote?.verse.verseText || '');
    setContent(editingNote?.content || '');
    setLinkedVerses(editingNote?.linkedVerses || []);
    setSelectedCategoryId(editingNote?.categoryId);
    setVerseHighlights(editingNote?.verseHighlights || []);
    setNoteHighlights(editingNote?.noteHighlights || []);
    setLinkedVerseInput('');
    setLinkedVerseText('');
    setLinkedVerseDesc('');
    setLinkedVerseHighlights([]);
    setNoteEditMode('edit');
    setError('');
  }, [editingNote]);

  const handleFetchMainVerse = async (refOverride?: string) => {
    const parsed = parseVerseReference(refOverride ?? verseInput);
    if (!parsed) {
      setError('Invalid verse format. Use format like "John 3:16"');
      return;
    }

    setIsLoadingVerse(true);
    setError('');
    try {
      const text = parsed.endVerse
        ? await fetchBibleVerseRange(parsed.book, parsed.chapter, parsed.verse, parsed.endVerse, bibleVersion)
        : await fetchBibleVerse(parsed.book, parsed.chapter, parsed.verse, bibleVersion);
      setVerseText(text);
    } catch (err) {
      setError('Failed to fetch verse. Please check the reference and try again.');
    } finally {
      setIsLoadingVerse(false);
    }
  };

  const handleFetchLinkedVerse = async (refOverride?: string) => {
    const parsed = parseVerseReference(refOverride ?? linkedVerseInput);
    if (!parsed) {
      setError('Invalid verse format. Use format like "John 3:16" or "Isaiah 42:1-4"');
      return;
    }

    setIsLoadingLinkedVerse(true);
    setError('');
    try {
      const text = parsed.endVerse
        ? await fetchBibleVerseRange(parsed.book, parsed.chapter, parsed.verse, parsed.endVerse, bibleVersion)
        : await fetchBibleVerse(parsed.book, parsed.chapter, parsed.verse, bibleVersion);
      setLinkedVerseText(text);
    } catch (err) {
      setError('Failed to fetch verse. Please check the reference and try again.');
    } finally {
      setIsLoadingLinkedVerse(false);
    }
  };

  const handleAddLinkedVerse = () => {
    const parsed = parseVerseReference(linkedVerseInput);
    if (!parsed) {
      setError('Invalid verse format. Use format like "John 3:16" or "Isaiah 42:1-4"');
      return;
    }
    
    if (linkedVerses.some(v => v.id === parsed.id)) {
      setError('This verse is already linked');
      return;
    }

    const newRef: VerseReference = {
      ...parsed,
      verseText: linkedVerseText || undefined,
      verseHighlights: linkedVerseHighlights.length ? linkedVerseHighlights : undefined,
      content: linkedVerseDesc.trim() || undefined,
      linkedVerses: [],
      isExpanded: false
    };

    setLinkedVerses([...linkedVerses, newRef]);
    setLinkedVerseInput('');
    setLinkedVerseText('');
    setLinkedVerseDesc('');
    setLinkedVerseHighlights([]);
    setError('');
  };

  const handleRemoveLinkedVerse = (id: string) => {
    setLinkedVerses(linkedVerses.filter(v => v.id !== id));
  };

  const handleSave = () => {
    const parsedVerse = parseVerseReference(verseInput);
    if (!parsedVerse) {
      setError('Invalid main verse format. Use format like "John 3:16"');
      return;
    }


    const note: StudyNote = {
      id: editingNote?.id || generateVerseId(),
      verse: { ...parsedVerse, verseText: verseText || undefined },
      content: content.trim(),
      linkedVerses,
      categoryId: selectedCategoryId,
      verseHighlights: verseHighlights.length ? verseHighlights : undefined,
      noteHighlights: noteHighlights.length ? noteHighlights : undefined,
      createdAt: editingNote?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    onSave(note);
    setVerseInput('');
    setVerseText('');
    setContent('');
    setLinkedVerses([]);
    setSelectedCategoryId(undefined);
    setVerseHighlights([]);
    setNoteHighlights([]);
    setError('');
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 mb-6">
      <h2 className="text-xl sm:text-2xl font-bold mb-4 text-gray-800">
        {editingNote ? 'Edit Note' : 'New Study Note'}
      </h2>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bible Version
          </label>
          <select
            value={bibleVersion}
            onChange={(e) => setBibleVersion(e.target.value as BibleVersion)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {BIBLE_VERSIONS.map((version) => (
              <option key={version.value} value={version.value}>
                {version.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Main Verse Reference
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={verseInput}
              onChange={(e) => setVerseInput(e.target.value)}
              placeholder="e.g., John 3:16"
              className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
            <button
              onClick={() => setShowMainPicker(true)}
              className="px-2 sm:px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors flex-shrink-0"
              title="Browse books"
            >
              <BookOpen size={18} />
            </button>
            <button
              onClick={() => handleFetchMainVerse()}
              disabled={isLoadingVerse || !verseInput}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 text-sm"
              title="Fetch verse text"
            >
              <Search size={16} />
              <span className="hidden xs:inline">{isLoadingVerse ? 'Loading...' : 'Fetch'}</span>
              <span className="xs:hidden">{isLoadingVerse ? '…' : 'Fetch'}</span>
            </button>
          </div>
          {showMainPicker && (
            <BiblePicker
              onSelect={(ref) => { setVerseInput(ref); setVerseText(''); setShowMainPicker(false); handleFetchMainVerse(ref); }}
              onClose={() => setShowMainPicker(false)}
            />
          )}
        </div>

        {verseText && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verse Text
              <span className="ml-2 text-xs font-normal text-gray-400">Select text to highlight</span>
            </label>
            <HighlightableText
              text={verseText}
              highlights={verseHighlights}
              onChange={setVerseHighlights}
              className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-gray-800 italic text-sm max-h-48 overflow-y-auto whitespace-pre-wrap"
            />
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">Study Notes</label>
            {content && (
              <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
                <button
                  type="button"
                  onClick={() => setNoteEditMode('edit')}
                  className={`px-3 py-1 transition-colors ${noteEditMode === 'edit' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >Edit</button>
                <button
                  type="button"
                  onClick={() => setNoteEditMode('highlight')}
                  className={`px-3 py-1 transition-colors ${noteEditMode === 'highlight' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >Highlight</button>
              </div>
            )}
          </div>
          {noteEditMode === 'highlight' && content ? (
            <div className="relative">
              <HighlightableText
                text={content}
                highlights={noteHighlights}
                onChange={setNoteHighlights}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm min-h-[9rem] whitespace-pre-wrap"
              />
              {noteHighlights.length > 0 && (
                <button
                  type="button"
                  onClick={() => setNoteHighlights([])}
                  className="absolute top-1 right-1 text-xs text-gray-400 hover:text-red-500 px-1"
                >clear all</button>
              )}
            </div>
          ) : (
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Enter your study notes here..."
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category (Optional)
          </label>
          <select
            value={selectedCategoryId || ''}
            onChange={(e) => setSelectedCategoryId(e.target.value || undefined)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">No category</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Linked References
          </label>
          <div className="space-y-2 mb-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={linkedVerseInput}
                onChange={(e) => setLinkedVerseInput(e.target.value)}
                placeholder="e.g., Romans 8:28"
                className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              <button
                onClick={() => setShowLinkedPicker(true)}
                className="px-2 sm:px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors flex-shrink-0"
                title="Browse books"
              >
                <BookOpen size={18} />
              </button>
              <button
                onClick={() => handleFetchLinkedVerse()}
                disabled={isLoadingLinkedVerse || !linkedVerseInput}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 text-sm"
                title="Fetch verse text"
              >
                <Search size={16} />
                {isLoadingLinkedVerse ? '…' : 'Fetch'}
              </button>
              {showLinkedPicker && (
                <BiblePicker
                  onSelect={(ref) => { setLinkedVerseInput(ref); setLinkedVerseText(''); setShowLinkedPicker(false); handleFetchLinkedVerse(ref); }}
                  onClose={() => setShowLinkedPicker(false)}
                />
              )}
            </div>
            {linkedVerseText && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-400">Select text to highlight</span>
                </div>
                <HighlightableText
                  text={linkedVerseText}
                  highlights={linkedVerseHighlights}
                  onChange={setLinkedVerseHighlights}
                  className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-gray-800 italic text-sm max-h-48 overflow-y-auto whitespace-pre-wrap"
                />
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2">
              <textarea
                value={linkedVerseDesc}
                onChange={(e) => setLinkedVerseDesc(e.target.value)}
                placeholder="Optional: Add your description/notes for this reference..."
                rows={3}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
              />
              <button
                onClick={handleAddLinkedVerse}
                disabled={!linkedVerseInput}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 sm:self-start disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                <Plus size={18} />
                Add
              </button>
            </div>
          </div>

          <div className="space-y-2">
            {linkedVerses.map((verse) => (
              <div
                key={verse.id}
                className="bg-blue-50 border border-blue-200 p-3 rounded-lg flex items-start justify-between"
              >
                <div className="flex-1">
                  <div className="font-semibold text-blue-700">{verse.display}</div>
                  {verse.verseText && (
                    <HighlightableText
                      text={verse.verseText}
                      highlights={verse.verseHighlights || []}
                      onChange={() => {}}
                      editable={false}
                      className="text-sm text-gray-700 mt-1 italic whitespace-pre-wrap"
                    />
                  )}
                  {verse.content && (
                    <div className="text-sm text-gray-600 mt-2 border-t border-blue-300 pt-2">
                      <span className="font-medium">Note: </span>{verse.content}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleRemoveLinkedVerse(verse.id)}
                  className="text-red-600 hover:bg-red-50 rounded p-1 flex-shrink-0"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button
            onClick={handleSave}
            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            {editingNote ? 'Update Note' : 'Save Note'}
          </button>
          {editingNote && (
            <button
              onClick={onCancel}
              className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-medium"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
